import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../environment';
import type { QosSubscriberGenerationMode, QosTrafficProfile } from './qos-project';
import { WorkerExecutionPreferenceService } from './worker-execution-preference.service';

export interface JobResultEnvelope<T = unknown> {
  result: T;
  artifact?: string;
}

export interface JobSubmitResponse {
  job_id: string;
}

export interface JobTimingCurrentStep {
  id: number;
  key: string | null;
  label: string | null;
  progress: number;
  started_at: string | null;
  elapsed_sec: number | null;
}

export interface JobTimingStatus {
  job_started_at: string | null;
  job_elapsed_sec: number | null;
  current_step: JobTimingCurrentStep | null;
  metrics: Record<string, number>;
}

export interface JobStatus {
  id: string;
  type: string;
  status: 'queued' | 'running' | 'done' | 'error' | 'canceled';
  progress: number;
  message: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  error: string | null;
  timing?: JobTimingStatus | null;
}

export interface PipelineChainStage {
  stage: string;
  job_id: string;
  status: string;
}

export interface PipelineChainInfo {
  job_id: string;
  root_job_id?: string | null;
  stages: PipelineChainStage[];
}

export interface VsubSubscriber {
  lon: number;
  lat: number;
  signal_dbm?: number | null;
  rsrp_proxy_dbm?: number | null;
  tower?: string | number | null;
  distance_m?: number | null;
  subscriber_id?: string | null;
  placement_type?: 'indoor' | 'outdoor' | 'roadside' | string;
  building_id?: string | null;
  floor_index?: number | null;
  floor_height_m?: number | null;
  device_height_m?: number | null;
  ue_z_local_m?: number | null;
  census_geoid?: string | null;
  census_vintage?: number | null;
  demand_source?: string | null;
}

export interface VsubTower {
  cell_id: string;
  lon: number;
  lat: number;
  radio: string;
  height_m: number | null;
  power_dbm: number | null;
  azimuth_deg: number | null;
  tilt_deg: number | null;
  tower_type: string | null;
  bands: string | null;
  samples: number;
}

export interface VsubBuilding {
  lon: number;
  lat: number;
  height: number;
  colour: string | null;
}

export interface VsubCoverageInfo {
  enabled: boolean;
  method: string;
  frequency_ghz: number;
  bandwidth_hz?: number;
  temperature_k?: number;
  receiver_noise_figure_db?: number;
  tx_count: number;
  max_depth: number;
  cell_size: [number, number];
  samples_per_tx: number;
  center: [number, number, number];
  size: [number, number];
  orientation?: [number, number, number];
  geo_bounds?: { west: number; south: number; east: number; north: number };
  grid_w_h: [number, number];
  metrics: {
    path_gain: { finite_fraction: number };
    rss_w: { finite_fraction: number };
    sinr?: { finite_fraction: number } | null;
  };
  radio_map_compute_sec?: number;
  radio_map_sec: number;
}

export interface VsubDemandResult {
  enabled: boolean;
  generation_mode?: QosSubscriberGenerationMode;
  model?: string;
  seed?: number;
  count?: number;
  count_source?: 'count' | 'density' | 'preview_artifact' | 'census_population' | string;
  density_per_km2?: number;
  area_km2?: number;
  generation_version?: number;
  legacy_mode?: string;
  market_share?: number;
  census_state_fips?: string;
  census_vintage?: number;
  census_unit_level?: string;
  subscriber_source_job_id?: string;
  weights?: {
    building: number;
    road: number;
    background: number;
  };
  effective_weights?: {
    building: number;
    road: number;
    background: number;
  };
  traffic_profile?: QosTrafficProfile;
  traffic_profile_definition?: {
    profile_key: QosTrafficProfile;
    profile_name: string;
    downlink_demand_mbps_per_active_subscriber: number;
    busy_hour_active_fraction: number;
    effective_downlink_demand_bps_per_subscriber: number;
    description?: string;
  };
}

export interface VsubSubscriberPreviewGeneration {
  mode: QosSubscriberGenerationMode;
  subscriber_model?: string | null;
  subscriber_count?: number | null;
  subscriber_density_per_km2?: number | null;
  subscriber_seed?: number | null;
  count_source?: string | null;
  market_share?: number | null;
  census_state_fips?: string | null;
  census_vintage?: number | null;
  census_unit_level?: string | null;
  generation_version?: number | null;
}

export interface VsubSubscriberPreviewAggregate {
  total_subscribers: number;
  placement_type_counts?: Record<string, number> | null;
  demand_source_counts?: Record<string, number> | null;
}

export interface VsubSubscriberPreviewResult {
  summary: string;
  type: string;
  bbox: { west: number; south: number; east: number; north: number };
  generation: VsubSubscriberPreviewGeneration;
  virtual_subscribers: VsubSubscriber[];
  aggregate: VsubSubscriberPreviewAggregate;
  warnings?: string[];
}

export interface VsubStageAPrepResult {
  summary: string;
  type: 'runpod_pipeline_v2_scene_prep';
  job_id: string;
  artifact_keys?: {
    scene_pack?: string;
    scene_pack_meta?: string;
    stage_meta?: string;
  };
  local_artifacts?: {
    scene_pack?: string;
    scene_pack_meta?: string;
    stage_meta?: string;
  };
}

export interface VsubSectorResult {
  cell_id: string;
  subscriber_count: number;
  avg_rsrp_proxy_dbm?: number | null;
  avg_sinr_db?: number | null;
  avg_delay_spread_s?: number | null;
  prb_demand?: number | null;
  prb_budget?: number | null;
  prbu_modeled_pct?: number | null;
  spectral_efficiency_method?: string | null;
  traffic_profile?: QosTrafficProfile | null;
}

export interface VsubSectorSummary {
  enabled: boolean;
  reason?: string;
  spectral_efficiency_method?: string;
  traffic_profile?: QosTrafficProfile | null;
  prb_budget?: number;
  effective_downlink_demand_bps_per_subscriber?: number;
  sector_count?: number;
  total_prb_demand?: number;
  peak_prbu_modeled_pct?: number;
  avg_prbu_modeled_pct?: number;
  over_capacity_sector_count?: number;
}

export interface VsubSectorResultsManifest {
  enabled: boolean;
  reason?: string;
  page_size?: number;
  total_rows?: number;
  page_count?: number;
  first_page_artifact?: string | null;
}

export interface VsubSectorResultsPage {
  page: number;
  page_size: number;
  total_rows: number;
  next_page_artifact?: string | null;
  rows: VsubSectorResult[];
}

export interface VsubSubscriberPathsSummary {
  requested: boolean;
  enabled: boolean;
  artifact_json?: string | null;
  subscriber_marker?: {
    asset_name?: string | null;
    scale_m?: number[];
    show_all_by_default?: boolean;
  } | null;
  reason?: string;
  requested_rx_count?: number;
  selected_rx_count?: number;
  exported_rx_count?: number;
  receivers_with_paths?: number;
  exported_path_count?: number;
  tx_count?: number;
  max_depth?: number;
  max_tx_per_rx?: number;
  max_paths_per_tx?: number;
  min_power_db?: number;
  truncated_rx_count?: number;
}

export interface VsubBaselineResult {
  summary: string;
  type: string;
  job_id?: string;
  bbox: { west: number; south: number; east: number; north: number };
  demand: VsubDemandResult;
  buildings_count: number;
  buildings?: VsubBuilding[];
  towers: VsubTower[];
  virtual_subscribers: VsubSubscriber[];
  aggregate: {
    total_subscribers: number;
    avg_signal_dbm: number;
    min_signal_dbm: number;
    max_signal_dbm: number;
  };
  sector_results?: VsubSectorResult[];
  sector_results_manifest?: VsubSectorResultsManifest | null;
  sector_summary?: VsubSectorSummary | null;
  scene_threejs_json?: string | null;
  models_dir?: string | null;
  subscriber_paths?: VsubSubscriberPathsSummary | null;
  coverage?: {
    path_gain_png: string;
    path_gain_raw_png?: string;
    rss_png: string;
    rss_raw_png?: string;
    sinr_png?: string;
    sinr_raw_png?: string;
    info: VsubCoverageInfo;
  } | null;
  timings: Record<string, number>;
  warnings?: string[];
}

@Injectable({ providedIn: 'root' })
export class SionnaJobService {
  private readonly http = inject(HttpClient);
  private readonly executionPreferences = inject(WorkerExecutionPreferenceService);
  private readonly baseUrl = environment.sionnaApiUrl;

  getConfiguredBaselineJobType(): string {
    const configured = (environment as Record<string, unknown>)['vsubBaselineJobType'];
    if (typeof configured === 'string' && configured.trim()) {
      return configured.trim();
    }
    return 'sionna_rt_vsub_baseline';
  }

  submitJob(type: string, params: Record<string, unknown>): Observable<JobSubmitResponse> {
    const executionPreference = this.executionPreferences.getExecutionPreference();
    const enrichedParams: Record<string, unknown> = {
      ...params,
      execution_preference: params['execution_preference'] ?? executionPreference,
    };
    return this.http.post<JobSubmitResponse>(`${this.baseUrl}/jobs`, { type, params: enrichedParams });
  }

  getStatus(jobId: string): Observable<JobStatus> {
    return this.http.get<JobStatus>(`${this.baseUrl}/jobs/${jobId}/status`);
  }

  getResult<T = unknown>(jobId: string): Observable<T> {
    return this.http.get<T>(`${this.baseUrl}/jobs/${jobId}/result`);
  }

  getResultEnvelope<T = unknown>(jobId: string): Observable<JobResultEnvelope<T>> {
    return this.http.get<JobResultEnvelope<T>>(`${this.baseUrl}/jobs/${jobId}/result`);
  }

  getJobLog(jobId: string): Observable<string> {
    return this.http.get(`${this.baseUrl}/jobs/${jobId}/log`, { responseType: 'text' });
  }

  getArtifactUrl(path: string): string {
    const normalized = String(path || '').trim();
    if (!normalized) {
      return this.baseUrl;
    }
    return `${this.baseUrl}/artifact?path=${encodeURIComponent(normalized)}`;
  }

  getPipelineChainInfo(jobId: string): Observable<PipelineChainInfo> {
    return this.http.get<PipelineChainInfo>(`${this.baseUrl}/api/pipeline/v2/chain-info/${encodeURIComponent(jobId)}`);
  }

  watchJobEvents(jobId: string): Observable<JobStatus> {
    return new Observable<JobStatus>((subscriber) => {
      const source = new EventSource(`${this.baseUrl}/jobs/${encodeURIComponent(jobId)}/events`);

      source.onmessage = (event) => {
        try {
          subscriber.next(JSON.parse(event.data) as JobStatus);
        } catch (error) {
          subscriber.error(error);
        }
      };

      source.onerror = () => {
        source.close();
        subscriber.complete();
      };

      return () => source.close();
    });
  }

  /**
   * Stream job status updates until completion using backend SSE events.
   */
  watchJobUntilDone(jobId: string): Observable<JobStatus> {
    return this.watchJobEvents(jobId);
  }
}

